import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Heart, FileDown, ShieldCheck, Plus, Minus } from "lucide-react";
import ProductCard from "@/components/ProductCard";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const DEFAULT_PRODUCT_IMAGE = "https://nivilabs.bionivid.com/resources/images/assest.static.default.product.png";

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const [selectedPackSize, setSelectedPackSize] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { user } = useAuth();
  const nav = useNavigate();

  const loadProduct = async () => {
    setLoading(true);
    setError("");
    setProduct(null);
    try {
      const response = await api.get(`/products/${id}`);
      setProduct(response.data);
    } catch (err) {
      setError("Failed to load product details. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProduct();
  }, [id]);
  useEffect(() => {
    if (!product) return;
    const sizes = Array.isArray(product.packSizes)
      ? product.packSizes
          .filter(Boolean)
          .map((item) => typeof item === "object" ? (item.catalogNumber || item.catalog_number || "") : "")
          .filter(Boolean)
      : [];
    setSelectedPackSize((current) => (sizes.includes(current) ? current : sizes[0] || null));
  }, [product]);

  const { add, items, setQty: setCartQty } = useCart();

  const [isWishlisted, setIsWishlisted] = useState(false);

  const addToWishlist = async () => {
    if (!user) { toast.message("Sign in to save products"); nav("/login"); return; }
    try {
      await api.post(`/wishlist/${product.id}`);
      setIsWishlisted(true);
      toast.success("Added to wishlist");
    } catch {
      toast.error("Could not add to wishlist");
    }
  };

  const removeFromWishlist = async () => {
    if (!user) { toast.message("Sign in to save products"); nav("/login"); return; }
    try {
      await api.delete(`/wishlist/${product.id}`);
      setIsWishlisted(false);
      toast.success("Removed");
    } catch {
      toast.error("Could not remove from wishlist");
    }
  };

  useEffect(() => {
    if (!product) return;
    const currentPack = Array.isArray(product.packSizes)
      ? product.packSizes.find((item) => (item?.catalogNumber || item?.catalog_number || "") === selectedPackSize)
      : null;
    const cartItem = items.find(
      (i) => i.product_id === product.id && i.selectedPackSize === selectedPackSize
    );
    if (cartItem) {
      setQty(cartItem.quantity);
    } else if (!currentPack) {
      setQty(1);
    }
  }, [items, product, selectedPackSize]);

  useEffect(() => {
    if (!user || !product) { setIsWishlisted(false); return; }
    let cancelled = false;
    api.get("/wishlist")
      .then((r) => {
        if (cancelled) return;
        const exists = Array.isArray(r.data) && r.data.some((p) => p.id === product.id);
        setIsWishlisted(Boolean(exists));
      })
      .catch(() => {
        if (!cancelled) setIsWishlisted(false);
      });
    return () => { cancelled = true; };
  }, [user, product]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-10" data-testid="product-detail-loading">
        <div className="animate-pulse h-96 bg-muted/40 rounded-2xl" />
        <div className="mt-4 text-sm text-muted-foreground">Loading product details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto p-10 text-center" data-testid="product-detail-error">
        <div className="inline-flex items-center justify-center h-24 w-24 rounded-full bg-destructive/10 text-destructive mx-auto mb-4">!</div>
        <h2 className="text-xl font-semibold">Unable to load product</h2>
        <p className="mt-2 text-sm text-muted-foreground">{error}</p>
        <button
          type="button"
          onClick={loadProduct}
          className="mt-4 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="max-w-7xl mx-auto p-10 text-sm text-muted-foreground" data-testid="product-detail-empty">
        Product details are not available.
      </div>
    );
  }

  const images = Array.isArray(product.images) && product.images.length
    ? product.images
    : [DEFAULT_PRODUCT_IMAGE];
  const objectProperty = images[0] === DEFAULT_PRODUCT_IMAGE ?  "cover" : "contain";
  const packSizes = Array.isArray(product.packSizes)
    ? product.packSizes.filter(Boolean)
    : [];

  const selectedPack = Array.isArray(product.packSizes)
    ? product.packSizes.find((item) => (item?.catalogNumber || item?.catalog_number || "") === selectedPackSize)
    : null;

  const cartItem = items.find(
    (i) => i.product_id === product.id && i.selectedPackSize === selectedPackSize
  );

  const updateQty = (newQty) => {
    const qtyValue = Math.max(1, Number(newQty) || 1);
    if (cartItem) {
      setCartQty(cartItem.line_id, qtyValue);
    }
    setQty(qtyValue);
  };

  const documents = Array.isArray(product.documents)
    ? product.documents
        .filter((doc) => doc && doc.url)
        .map((doc) => ({
          ...doc,
          url: doc.url.startsWith("http") ? doc.url : `${BACKEND_URL}/${doc.url}`,
        }))
    : [];
  console.log(packSizes)
  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="product-detail-page">
      <div className="text-xs text-muted-foreground mb-5">
        <Link to="/" className="hover:text-primary">Home</Link> /
        <Link to={`/products?category=${product.category_slug}`} className="hover:text-primary"> {product.category_slug}</Link> /
        <span className="text-foreground"> {product.name}</span>
      </div>

      <div className="grid lg:grid-cols-2 gap-12">
        {/* Gallery */}
        <div>
          <div className="aspect-square bg-white border border-gray-100 rounded-2xl overflow-hidden">
            <img src={images[activeImg] || DEFAULT_PRODUCT_IMAGE} alt={product.name} className={`w-full h-full object-${objectProperty}`} />
          </div>
          {images.length > 1 && (
            <div className="mt-3 grid grid-cols-5 gap-2">
              {images.map((img, i) => (
                <button key={i} onClick={() => setActiveImg(i)} className={`aspect-square rounded-md overflow-hidden border ${activeImg === i ? "border-primary" : "border-gray-100"}`}>
                  <img src={img || DEFAULT_PRODUCT_IMAGE} alt="" className={`w-full h-full object-${objectProperty}`} />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            {product.new_arrival && <Badge className="bg-secondary/10 text-secondary border-0">NEW</Badge>}
            {product.featured && <Badge className="bg-accent/30 text-accent-foreground border-0">FEATURED</Badge>}
            {/* <span className={`text-xs font-medium ${product.availability === "in_stock" ? "text-primary" : "text-destructive"}`}>
              ● {product.availability === "in_stock" ? "In stock" : "Out of stock"}
            </span> */}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold mt-3 leading-tight">{product.name}</h1>
          <div className="mt-2 font-mono text-sm text-muted-foreground">Catalog No: {selectedPack?.catalogNumber || selectedPack?.catalog_number || product.catalogNumber || "—"}</div>
          {product.hsnCode ? (
            <div className="mt-1 text-sm text-muted-foreground" data-testid="product-hsn-code">
              HSN Code: {product.hsnCode}
            </div>
          ) : null}
          {selectedPack ? (
            <div className="mt-1 text-sm text-muted-foreground space-y-1">
              <div>
                Qty available: {selectedPack.inventoryQty != null ? selectedPack.inventoryQty : selectedPack.inventory_qty != null ? selectedPack.inventory_qty : "—"}
                <span className="mx-2">·</span>
                <span className={`${selectedPack.availability === "in_stock" ? "text-primary" : "text-destructive"}`}>
                  {selectedPack.availability ? selectedPack.availability.replace(/_/g, " ") : "Availability unknown"}
                </span>
              </div>
              <div className="font-mono text-sm text-muted-foreground">Pack info: {selectedPack.packInfo || selectedPack.pack_info || "NA"}</div>
            </div>
          ) : (
            <div className="mt-1 text-sm text-muted-foreground">Select a pack size to view catalog number and availability.</div>
          )}
          {product.gemLink ? (
            <div className="mt-2 text-sm">
              <a
                href={product.gemLink.startsWith("http") ? product.gemLink : `https://${product.gemLink}`}
                target="_blank"
                rel="noreferrer"
                className="text-primary underline hover:text-primary/80"
                data-testid="product-gem-link"
              >
                View GEM link
              </a>
            </div>
          ) : (
            <div className="mt-2 text-sm text-muted-foreground">No GEM link is available for this product.</div>
          )}
          <p className="mt-4 text-muted-foreground leading-relaxed">{product.description || "No description is available for this product."}</p>

          <div className="mt-6">
            <div className="text-sm font-semibold text-foreground mb-2">Pack size</div>
            {packSizes.length > 0 ? (
              <div className="grid grid-cols-3 gap-2">
                {packSizes.map((packObj) => {
                  const catalogNum = packObj?.catalogNumber || packObj?.catalog_number || "";
                  const packSizeDisplay = packObj?.packSize || packObj?.pack_size || "";
                  return (
                    <button
                      key={catalogNum}
                      type="button"
                      onClick={() => setSelectedPackSize(catalogNum)}
                      className={`rounded-xl border px-3 py-2 text-sm font-medium transition ${selectedPackSize === catalogNum ? "border-primary bg-primary text-white" : "border-gray-200 bg-white text-foreground hover:border-primary"}`}
                      data-testid={`packsize-${packSizeDisplay}`}
                    >
                      {packSizeDisplay}
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">No pack size options are available for this product.</div>
            )}
          </div>

          <div className="mt-6 flex items-center gap-3">
            <div className="flex items-center border border-gray-200 rounded-md overflow-hidden">
              <button onClick={() => updateQty(qty - 1)} className="w-10 h-11 hover:bg-muted" data-testid="pdp-qty-minus"><Minus className="w-4 h-4 m-auto" /></button>
              <input type="number" min={1} value={qty} onChange={(e) => updateQty(e.target.value)} className="w-14 h-11 text-center outline-none" data-testid="pdp-qty-input" />
              <button onClick={() => updateQty(qty + 1)} className="w-10 h-11 hover:bg-muted" data-testid="pdp-qty-plus"><Plus className="w-4 h-4 m-auto" /></button>
            </div>
            <Button onClick={() => add(product, qty, selectedPack?.catalogNumber || selectedPack?.catalog_number || null, selectedPackSize)} size="lg" className="flex-1 bg-primary hover:bg-primary/90 h-11" data-testid="pdp-add-to-inquiry">
              Add to inquiry
            </Button>
            <Button variant="outline" size="lg" onClick={isWishlisted ? removeFromWishlist : addToWishlist} className="h-11" data-testid="pdp-wishlist">
              <Heart className={`w-4 h-4 ${isWishlisted ? "text-destructive" : ""}`} />
            </Button>
          </div>

          <div className="mt-6 grid sm:grid-cols-2 gap-3">
            <div className="flex items-start gap-2 text-sm">
              <ShieldCheck className="w-5 h-5 text-primary mt-0.5" />
              <div>
                <div className="font-medium">Quality assured</div>
                <div className="text-xs text-muted-foreground">CoA & lot traceability included</div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <Tabs defaultValue="specifications" className="mt-8">
            <TabsList className="bg-muted/50">
              <TabsTrigger value="specifications" data-testid="tab-specs">Specifications</TabsTrigger>
              <TabsTrigger value="technical-references" data-testid="tab-technical-references">Technical References</TabsTrigger>
            </TabsList>
            <TabsContent value="specifications" className="pt-4">
              {Object.keys(product.specifications || {}).length === 0 ? (
                <div className="text-sm text-muted-foreground">No specifications are available for this product.</div>
              ) : (
                <table className="w-full text-sm border-collapse">
                  <tbody>
                    {Object.entries(product.specifications || {}).map(([k, v]) => (
                      <tr key={k} className="border-b border-gray-100">
                        <td className="py-2.5 text-muted-foreground w-1/3">{k}</td>
                        <td className="py-2.5 font-medium">{v}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </TabsContent>
            <TabsContent value="technical-references" className="pt-4">
              {documents.length > 0 ? (
                <div className="space-y-3">
                  {documents.map((document) => (
                    <a
                      key={document.url}
                      href={document.url}
                      download={document.name}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-medium text-foreground shadow-sm transition hover:border-primary/50"
                      data-testid={`document-${document.name}`}
                    >
                      <div>
                        <div>{document.name}</div>
                        <p className="text-xs text-muted-foreground">Download PDF</p>
                      </div>
                      <FileDown className="w-5 h-5 text-primary" />
                    </a>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">No technical resources available for this product.</div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {product.related?.length > 0 && (
        <section className="mt-20" data-testid="related-section">
          <h2 className="font-display text-2xl sm:text-3xl font-bold mb-6">Related products</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 lg:gap-6">
            {product.related.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}
    </main>
  );
}
